import { usePurchaseContext } from '~/purchase/context';
import { useI18n, Trans } from '~/common/context';
import { RecordValues, ValidationErrorResponse } from '@wyz/types';
import { Button, Card, Container, Flex, Text, WizardSteps } from '@wyz/ui';
import { invariant } from '@wyz/utils';
import { useState } from 'react';
import { CommonErrorCodesEnumType } from '~/common/types';
import {
  QuoteServiceApplicationStep,
  QuoteServiceGeometryStepForm,
  QuoteServiceMountingStepForm,
  useQuoteServicePricingPolicyForm,
} from '~/purchase/components';
import { QuoteServicePricingPolicyHelpers } from '~/purchase/helpers';
import { usePolicyFormStepper } from '~/purchase/hooks';
import {
  PRICING_STATUS_ENUM,
  QUOTE_SERVICE_PRICING_POLICY_FORM_STEPS,
} from '~/purchase/schemas';
import {
  IGlobalQuoteServicePricingFormData,
  IPricingStatus,
  IQuoteServicePricingPolicyFormStep,
} from '~/purchase/types';

type Step = RecordValues<
  typeof QuoteServicePricingPolicyHelpers.SERVICE_PRICING_POLICY_STEP_INDEX
>;

type Props = {
  onSubmit: (
    formData: IGlobalQuoteServicePricingFormData,
    status: IPricingStatus,
  ) => void;
  defaultValues?: IGlobalQuoteServicePricingFormData;
  isSubmitting: boolean;
  policyGroupId?: number;
  errors?: ValidationErrorResponse<CommonErrorCodesEnumType> | null;
};

function QuoteServicePricingPoliciesForm({
  onSubmit,
  defaultValues,
  isSubmitting,
  policyGroupId,
  errors,
}: Props) {
  const pricingPolicyForm = useQuoteServicePricingPolicyForm();
  const { state } = usePurchaseContext();
  const { t } = useI18n();
  invariant(state.targetCustomer);
  const [formState, setFormState] =
    useState<IGlobalQuoteServicePricingFormData>(
      defaultValues ??
        QuoteServicePricingPolicyHelpers.getInitialValues({
          vehicleTypeGroups: pricingPolicyForm.vehicleTypeGroups.optionItems,
          t,
        }),
    );

  const [currentStepIndex, stepperHandlers] = usePolicyFormStepper<Step>({});
  const currentStepKey =
    QUOTE_SERVICE_PRICING_POLICY_FORM_STEPS[currentStepIndex];

  return (
    <>
      <WizardSteps
        steps={(
          Object.keys(
            QuoteServicePricingPolicyHelpers.SERVICE_PRICING_POLICY_STEP_INDEX,
          ) as Array<IQuoteServicePricingPolicyFormStep>
        ).map((stepKey) => ({
          id: stepKey,
          label: t(
            QuoteServicePricingPolicyHelpers.SERVICE_PRICING_POLICY_STEP_LABELS[
              stepKey
            ],
          ),
        }))}
        activeStep={currentStepIndex}
        onSelectStep={() => {}}
      />
      <Container>
        {currentStepIndex ==
          QuoteServicePricingPolicyHelpers.SERVICE_PRICING_POLICY_STEP_INDEX
            .MOUNTING && (
          <QuoteServiceMountingStepForm
            globalFormData={formState}
            errors={errors}
            onSubmit={(formData) => {
              stepperHandlers.onNext();
              setFormState((prev) => ({
                ...prev,
                MOUNTING: formData,
              }));
            }}
          />
        )}

        {currentStepIndex ==
          QuoteServicePricingPolicyHelpers.SERVICE_PRICING_POLICY_STEP_INDEX
            .APPLICATION && (
          <QuoteServiceApplicationStep
            customerId={state.targetCustomer.id}
            globalFormData={formState}
            errors={errors}
            onSubmit={(formData) => {
              stepperHandlers.onNext();
              setFormState((prev) => ({ ...prev, APPLICATION: formData }));
            }}
            policyGroupId={policyGroupId}
          />
        )}
        {currentStepIndex ==
          QuoteServicePricingPolicyHelpers.SERVICE_PRICING_POLICY_STEP_INDEX
            .GEOMETRY_SERVICE && (
          <QuoteServiceGeometryStepForm
            globalFormData={formState}
            errors={errors}
            onSubmit={(formData, status) => {
              setFormState((prev) => {
                const nextState = { ...prev, GEOMETRY_SERVICE: formData };
                onSubmit(nextState, status);
                return nextState;
              });
            }}
          />
        )}
        <Card className='sticky' style={{ bottom: '5%' }}>
          <Card.Body>
            <Flex justifyContent={'between'}>
              <Button
                type='button'
                id='previous'
                variant='link'
                disabled={
                  currentStepIndex ===
                    QuoteServicePricingPolicyHelpers.FIRST_STEP || isSubmitting
                }
                onClick={stepperHandlers.onPrevious}
              >
                <Trans i18nKey='app.common.button.previous' />
              </Button>
              <Flex className='space-x-2' alignItems='center'>
                <Text heading='sm' color='secondary'>
                  {formState.MOUNTING?.name}
                </Text>
                <Text heading='sm' color='secondary-gray'>
                  <Trans
                    i18nKey='app.common.step_value'
                    values={{
                      stepNumber: currentStepIndex + 1,
                      stepName: t(
                        QuoteServicePricingPolicyHelpers
                          .SERVICE_PRICING_POLICY_STEP_LABELS[currentStepKey],
                      ),
                    }}
                  />
                </Text>
                {currentStepIndex ===
                  QuoteServicePricingPolicyHelpers.LAST_STEP && (
                  <Button
                    id={'save-as-draft'}
                    data-status={PRICING_STATUS_ENUM.DRAFT}
                    variant='secondary'
                    form={currentStepIndex.toString()}
                    type='submit'
                    children={
                      <Trans i18nKey='app.common.button.save_as_draft' />
                    }
                    isLoading={isSubmitting}
                  />
                )}
                <Button
                  id='next'
                  data-status={PRICING_STATUS_ENUM.ENABLED}
                  variant='primary'
                  type='submit'
                  form={currentStepIndex.toString()}
                  children={
                    currentStepIndex <
                    QuoteServicePricingPolicyHelpers.LAST_STEP
                      ? t('app.common.buttons.continue')
                      : t('app.common.button.save')
                  }
                  isLoading={isSubmitting}
                />
              </Flex>
            </Flex>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}

export default QuoteServicePricingPoliciesForm;
